<template>
  <div class="cep-system-message">
    <div class="cep-system-avatar">N</div>
    <div class="cep-system-message-body">
      <div class="cep-system-bubble">
        <template v-if="content && content.length">
          <div v-for="(el, i) in content" :key="i" class="cep-content-block">

            <!-- text -->
            <p v-if="el.type === 'text'" class="cep-text" v-html="renderText(el.value)" />

            <!-- tags: checkmark badges -->
            <div v-else-if="el.type === 'tags'" class="cep-tags">
              <span v-for="(tag, j) in el.items" :key="j" class="cep-tag">
                <span class="cep-tag-check">✓</span> {{ tag }}
              </span>
            </div>

            <!-- suggestion: labelled bold line -->
            <p v-else-if="el.type === 'suggestion'" class="cep-suggestion">
              {{ el.label }} <strong>{{ el.value }}</strong>
            </p>

            <!-- buttons: action row -->
            <div v-else-if="el.type === 'buttons'" class="cep-buttons">
              <button
                v-for="(btn, j) in el.items"
                :key="j"
                :class="['cep-btn', btn.variant === 'primary' ? 'cep-btn-primary' : 'cep-btn-outline']"
                :disabled="!isLast || btn.disabled"
                @click="$emit('action', { type: 'button', value: btn.value, elementIndex: i })"
              >{{ btn.label }}</button>
            </div>

            <!-- chips: selectable pills (single select) -->
            <div v-else-if="el.type === 'chips'" class="cep-chips">
              <button
                v-for="(chip, j) in el.items"
                :key="j"
                :class="['cep-chip', chipVariantClass(chip), el.selected === chipValue(chip) ? 'cep-chip-selected' : '']"
                :disabled="!isLast || chipDisabled(chip)"
                @click="$emit('action', { type: 'chip', value: chipValue(chip), elementIndex: i })"
              >
                <span v-if="chipSubtitle(chip)" class="cep-chip-label">{{ chipLabel(chip) }}</span>
                <template v-else>{{ chipLabel(chip) }}</template>
                <span v-if="chipSubtitle(chip)" class="cep-chip-subtitle">{{ chipSubtitle(chip) }}</span>
              </button>
            </div>

            <!-- multi_chips: multi-select pills with confirm -->
            <div v-else-if="el.type === 'multi_chips'" class="cep-chips-multi">
              <div class="cep-chips">
                <button
                  v-for="(chip, j) in el.items"
                  :key="j"
                  :class="['cep-chip', multiSelections[i] && multiSelections[i].includes(chip) ? 'cep-chip-selected' : '']"
                  :disabled="!isLast"
                  @click="toggleMultiChip(i, chip)"
                >{{ chip }}</button>
              </div>
              <button
                class="cep-btn cep-btn-primary cep-multi-confirm"
                :disabled="!isLast || !multiSelections[i] || !multiSelections[i].length"
                @click="$emit('action', { type: 'chip-multi-confirm', value: multiSelections[i], elementIndex: i })"
              >{{ el.confirmButton }}</button>
            </div>

            <!-- cards: selectable cards with optional confirm button -->
            <div v-else-if="el.type === 'cards'" class="cep-cards">
              <template v-if="el.forceCards || el.items.length <= 5">
                <div :class="['cep-cards-list', { 'cep-cards-list-scroll': el.items.length > 5 }]">
                  <div
                    v-for="(card, j) in el.items"
                    :key="j"
                    :class="['cep-card', cardSelections[i] === j ? 'cep-card-selected' : '', !isLast ? 'cep-card-disabled' : '']"
                    @click="isLast && $set(cardSelections, i, j)"
                  >
                    <strong>{{ card.title }}</strong>
                    <span v-if="card.subtitle" class="cep-card-subtitle">{{ card.subtitle }}</span>
                    <p v-if="card.preview" class="cep-card-preview">{{ card.preview }}</p>
                    <!-- Optional media thumbnail (card.media), e.g. a template header file. -->
                    <media-preview
                      v-if="card.media"
                      compact
                      :show-caption="false"
                      :format="card.media.format"
                      :media-id="card.media.mediaId"
                      :media-name="card.media.mediaName || ''"
                      :file-name="card.media.fileName || ''"
                      :country-id="card.media.countryId"
                      :language-id="card.media.languageId"
                    />
                  </div>
                </div>
              </template>
              <template v-else>
                <nv-select
                  class="cep-form-select cep-card-dropdown"
                  :disabled="!isLast"
                  :value="cardSelections[i] !== undefined ? cardSelections[i] : ''"
                  @change="isLast && $set(cardSelections, i, parseInt($event.target.value))"
                >
                  <option value="" disabled>Select an option…</option>
                  <option v-for="(card, j) in el.items" :key="j" :value="j">{{ card.title }}</option>
                </nv-select>
                <div v-if="cardSelections[i] !== undefined && el.items[cardSelections[i]] && (el.items[cardSelections[i]].subtitle || el.items[cardSelections[i]].preview)" class="cep-card-dropdown-preview">
                  <span v-if="el.items[cardSelections[i]].subtitle" class="cep-card-subtitle">{{ el.items[cardSelections[i]].subtitle }}</span>
                  <p v-if="el.items[cardSelections[i]].preview" class="cep-card-preview">{{ el.items[cardSelections[i]].preview }}</p>
                </div>
              </template>
              <div v-if="el.confirmButton" class="cep-buttons">
                <button
                  class="cep-btn cep-btn-primary"
                  :disabled="!isLast || cardSelections[i] === undefined"
                  @click="$emit('action', { type: 'card-confirm', value: cardSelections[i], elementIndex: i })"
                >{{ el.confirmButton }}</button>
                <span v-if="isLast && cardSelections[i] === undefined" class="cep-card-hint">Select an option above first</span>
              </div>
            </div>

            <!-- badge-row: inline key:value badges -->
            <div v-else-if="el.type === 'badge-row'" class="cep-badge-row">
              <span v-for="(badge, j) in el.items" :key="j" class="cep-badge">
                {{ badge.label }}: <strong>{{ badge.value }}</strong>
              </span>
            </div>

            <!-- config: labelled key-value pairs -->
            <div v-else-if="el.type === 'config'" class="cep-config">
              <div v-for="(item, j) in el.items" :key="j" class="cep-config-row">
                <span class="cep-config-label">{{ item.label }}:</span>
                <span class="cep-config-value">{{ item.value }}</span>
              </div>
            </div>

            <!-- form: grid of inputs/selects with submit button -->
            <div v-else-if="el.type === 'form'" class="cep-form">
              <div class="cep-form-grid">
                <div v-for="(field, j) in el.fields" :key="j" class="cep-form-field">
                  <label class="cep-form-label">{{ field.label }}</label>
                  <nv-select v-if="field.type === 'select'" v-model="formState[i][field.name]" class="cep-form-select">
                    <option value="" disabled>{{ field.placeholder || 'Select…' }}</option>
                    <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
                  </nv-select>
                  <input
                    v-else
                    v-model="formState[i][field.name]"
                    :placeholder="field.placeholder || ''"
                    class="cep-form-input"
                  />
                </div>
              </div>
              <button
                class="cep-btn cep-btn-primary"
                :disabled="!isLast"
                @click="$emit('action', { type: 'form', value: formState[i], elementIndex: i })"
              >{{ el.button }}</button>
            </div>

            <!-- content-editor: per-language editable nudge copy with char count -->
            <div v-else-if="el.type === 'content-editor'" class="cep-content-editor">
              <div v-for="lang in el.languages" :key="lang" class="cep-lang-block">
                <div class="cep-lang-name">{{ lang }}</div>
                <textarea
                  v-model="editorState[i][lang]"
                  class="cep-lang-textarea"
                  rows="4"
                  :disabled="!isLast || el.readOnly"
                ></textarea>
                <div class="cep-lang-meta">
                  <span class="cep-char-count">{{ (editorState[i][lang] || '').length }} / 1024 chars</span>
                  <span class="cep-flag-ok">✓ Clinical check passed</span>
                </div>
              </div>
              <!-- Approved-template content is fixed — no edit/regenerate, just confirm. -->
              <template v-if="!el.readOnly">
                <div class="cep-regen-row">
                  <input
                    v-model="regenNote[i]"
                    type="text"
                    class="cep-form-input cep-regen-input"
                    placeholder="Optional: tell it what to change, e.g. &quot;make it shorter&quot;"
                    :disabled="!isLast"
                    @keyup.enter="isLast && $emit('action', { type: 'content-regenerate', value: regenNote[i], elementIndex: i })"
                  />
                </div>
                <div class="cep-buttons">
                  <button
                    class="cep-btn cep-btn-outline"
                    :disabled="!isLast"
                    @click="$emit('action', { type: 'content-regenerate', value: regenNote[i], elementIndex: i })"
                  >🔄 Regenerate</button>
                  <button
                    class="cep-btn cep-btn-primary"
                    :disabled="!isLast"
                    @click="$emit('action', { type: 'content-confirm', value: editorState[i], elementIndex: i })"
                  >{{ el.confirmButton }}</button>
                </div>
              </template>
              <div v-else class="cep-buttons">
                <button
                  class="cep-btn cep-btn-primary"
                  :disabled="!isLast"
                  @click="$emit('action', { type: 'content-confirm', value: editorState[i], elementIndex: i })"
                >{{ el.confirmButton }}</button>
              </div>
            </div>

            <!-- upload: dropzone + optional skip -->
            <div v-else-if="el.type === 'upload'" class="cep-upload">
              <div
                class="cep-upload-zone"
                @click="$refs['upload-' + i][0].click()"
                @dragover.prevent
                @drop.prevent="handleDrop($event, i)"
              >
                <span class="cep-upload-icon">🖼</span>
                <span>
                  <strong class="cep-upload-link">{{ el.label }}</strong>
                  {{ el.hint ? ' or drag & drop — ' + el.hint : '' }}
                </span>
                <input
                  :ref="'upload-' + i"
                  type="file"
                  :accept="el.accept || 'image/*'"
                  class="cep-upload-input"
                  @change="handleFileChange($event, i)"
                />
              </div>
              <button
                v-if="el.skipLabel"
                class="cep-btn cep-btn-outline cep-upload-skip"
                @click="$emit('action', { type: 'upload-skip', elementIndex: i })"
              >{{ el.skipLabel }}</button>
            </div>

            <!-- media: a stored Media Management file, shown as it would be sent -->
            <div v-else-if="el.type === 'media'" class="cep-media">
              <media-preview
                :format="el.media.format"
                :media-id="el.media.mediaId"
                :media-name="el.media.mediaName || ''"
                :file-name="el.media.fileName || ''"
                :country-id="el.media.countryId"
                :language-id="el.media.languageId"
              />
            </div>

            <!-- link: internal navigation link (e.g. "View in Dashboard") -->
            <router-link v-else-if="el.type === 'link'" :to="el.to" target="_blank" rel="noopener" class="cep-link">{{ el.label }}</router-link>

          </div>
        </template>
        <span v-else-if="streaming && !text" class="cep-typing">
          <span class="cep-typing-dot" /><span class="cep-typing-dot" /><span class="cep-typing-dot" />
        </span>
        <span v-else>{{ text }}</span>
      </div>
      <div v-if="isDebugMode" class="cep-debug-bar">
        <span class="cep-debug-time">{{ formattedTime }}</span>
        <button class="cep-debug-copy" :class="{ copied: copySuccess }" :title="copySuccess ? 'Copied' : 'Copy'" @click="copyMessage">
          <svg v-if="!copySuccess" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import MediaPreview from '../MediaPreview.vue'

const IS_DEBUG = process.env.BUILD_TYPE === 'dev'

export default {
  name: 'SystemMessage',
  components: { MediaPreview },
  props: {
    text: {
      type: String,
      default: ''
    },
    content: {
      type: Array,
      default: null
    },
    isLast: {
      type: Boolean,
      default: false
    },
    streaming: {
      type: Boolean,
      default: false
    },
    timestamp: {
      type: Number,
      default: null
    }
  },
  emits: ['action'],
  data () {
    return {
      formState: {},
      cardSelections: {},
      multiSelections: {},
      editorState: {},
      regenNote: {},
      copySuccess: false
    }
  },
  computed: {
    isDebugMode () {
      return IS_DEBUG
    },
    formattedTime () {
      if (!this.timestamp) return ''
      return new Date(this.timestamp).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3
      })
    }
  },
  watch: {
    content: {
      immediate: true,
      handler (val) {
        if (!val) return
        val.forEach((el, i) => {
          if (el.type === 'form' && !this.formState[i]) {
            const state = {}
            el.fields.forEach(f => { state[f.name] = f.default || '' })
            this.$set(this.formState, i, state)
          }
          // Optional preselection (e.g. a country's languages), still editable before confirming.
          if (el.type === 'multi_chips' && Array.isArray(el.selected) && !this.multiSelections[i]) {
            this.$set(this.multiSelections, i, el.selected.filter(chip => (el.items || []).includes(chip)))
          }
          if (el.type === 'content-editor' && !this.editorState[i]) {
            this.$set(this.editorState, i, { ...(el.initial || {}) })
            this.$set(this.regenNote, i, '')
          }
        })
      }
    }
  },
  methods: {
    renderText (value) {
      const escaped = (value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
      return escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    },
    chipLabel (chip) {
      return typeof chip === 'string' ? chip : chip.label
    },
    chipSubtitle (chip) {
      return typeof chip === 'string' ? null : chip.subtitle
    },
    chipValue (chip) {
      if (typeof chip === 'string') return chip
      return chip.value !== undefined ? chip.value : chip.label
    },
    chipVariantClass (chip) {
      if (typeof chip === 'string') return ''
      return chip.variant === 'teal' ? 'cep-chip-teal' : 'cep-chip-pink'
    },
    chipDisabled (chip) {
      return typeof chip === 'string' ? false : !!chip.disabled
    },
    toggleMultiChip (index, chip) {
      if (!this.isLast) return
      const current = this.multiSelections[index] || []
      const updated = current.includes(chip)
        ? current.filter(c => c !== chip)
        : [...current, chip]
      this.$set(this.multiSelections, index, updated)
    },
    handleDrop (event, index) {
      const file = event.dataTransfer.files[0]
      if (file) this.$emit('action', { type: 'upload', value: file, elementIndex: index })
    },
    handleFileChange (event, index) {
      const file = event.target.files[0]
      if (file) this.$emit('action', { type: 'upload', value: file, elementIndex: index })
    },
    extractPlainText () {
      if (this.text) return this.text
      if (!this.content) return ''
      return this.content
        .filter(el => el.type === 'text')
        .map(el => el.value || '')
        .join('\n')
    },
    copyMessage () {
      navigator.clipboard.writeText(this.extractPlainText()).then(() => {
        this.copySuccess = true
        setTimeout(() => { this.copySuccess = false }, 2000)
      })
    }
  }
}
</script>

<style scoped>
.cep-system-message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
}

.cep-system-message-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  max-width: 50%;
}

.cep-system-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #0D9488;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cep-system-bubble {
  background: #f5f5f5;
  border-radius: 0 12px 12px 12px;
  padding: 12px 16px;
  font-size: 14px;
  color: #1a1a2e;
  line-height: 1.5;
  width: 100%;
}

.cep-debug-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
}

.cep-debug-time {
  font-size: 11px;
  color: #888;
  font-family: monospace;
}

.cep-debug-copy {
  font-size: 11px;
  color: #0D9488;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-weight: 500;
}

.cep-debug-copy.copied {
  color: #4caf50;
}

.cep-content-block + .cep-content-block {
  margin-top: 10px;
}

/* text */
.cep-text {
  margin: 0;
}

/* suggestion */
.cep-suggestion {
  margin: 0;
  color: #444;
}

/* tags */
.cep-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.cep-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #e6f9ec;
  color: #1a7a3c;
  border: 1px solid #b2dfca;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 500;
}

.cep-tag-check {
  font-size: 11px;
}

/* buttons */
.cep-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cep-btn {
  border: none;
  border-radius: 20px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.cep-btn-primary {
  background: #0D9488;
  color: #fff;
}

.cep-btn-primary:hover:not(:disabled) {
  background: #0B7C72;
}

.cep-btn-primary:disabled {
  background: #B9E3DF;
  cursor: not-allowed;
}

.cep-btn-outline {
  background: #fff;
  color: #0D9488;
  border: 1.5px solid #0D9488;
}

.cep-btn-outline:hover:not(:disabled) {
  background: #E6F5F4;
}

.cep-btn-outline:disabled {
  color: #9bd8e2;
  border-color: #B9E3DF;
  cursor: not-allowed;
}

/* chips */
.cep-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cep-chip {
  background: #fff;
  color: #1a1a2e;
  border: 1.5px solid #ccc;
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.cep-chip:hover {
  border-color: #0D9488;
}

.cep-chip-selected {
  border-color: #0D9488;
  background: #E6F5F4;
  color: #1E3A5F;
  font-weight: 500;
}

/* chip variants (label + subtitle) */
.cep-chip-label {
  display: block;
  font-weight: 600;
}

.cep-chip-subtitle {
  display: block;
  font-size: 10px;
  font-weight: 400;
  opacity: 0.85;
  margin-top: 1px;
}

.cep-chip-pink {
  border-color: #e8305a;
  color: #e8305a;
}

.cep-chip-pink:hover:not(:disabled) {
  background: #e8305a;
  color: #fff;
}

.cep-chip-pink.cep-chip-selected {
  border-color: #e8305a;
  background: #e8305a;
  color: #fff;
}

.cep-chip-teal {
  border-color: #0B7C72;
  color: #0B7C72;
}

.cep-chip-teal:hover:not(:disabled) {
  background: #0B7C72;
  color: #fff;
}

.cep-chip-teal.cep-chip-selected {
  border-color: #0B7C72;
  background: #0B7C72;
  color: #fff;
}

/* cards */
.cep-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cep-cards-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cep-cards-list-scroll {
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.cep-card {
  background: #fff;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  transition: border-color 0.15s, background 0.15s;
}

.cep-card:hover {
  border-color: #0D9488;
}

.cep-card-selected {
  border-color: #0D9488;
  background: #E6F5F4;
}

.cep-card-subtitle {
  font-size: 12px;
  color: #666;
}

.cep-card-preview {
  margin: 4px 0 0;
  font-size: 12px;
  color: #888;
  white-space: pre-wrap;
  font-style: italic;
}

.cep-card-disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.cep-card-dropdown {
  width: 100%;
}

.cep-card-dropdown-preview {
  margin-top: 8px;
  padding: 8px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
}

.cep-card-hint {
  align-self: center;
  font-size: 12px;
  color: #888;
  font-style: italic;
}

.cep-chips-multi {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cep-multi-confirm {
  align-self: flex-start;
  border-radius: 20px;
  padding: 7px 16px;
  font-size: 13px;
}

.cep-multi-confirm:disabled {
  background: #B9E3DF;
  cursor: not-allowed;
}

/* badge-row */
.cep-badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.cep-badge {
  background: #e0f2f1;
  color: #004d40;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
}

/* config */
.cep-config {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cep-config-row {
  font-size: 13px;
  display: flex;
  gap: 4px;
}

.cep-config-label {
  font-weight: 600;
  color: #333;
}

.cep-config-value {
  color: #1a1a2e;
}

/* form */
.cep-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cep-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.cep-form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cep-form-label {
  font-size: 12px;
  color: #555;
  font-weight: 500;
}

.cep-form-select,
.cep-form-input {
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  color: #1a1a2e;
  background: #fff;
  outline: none;
}

.cep-form-select:focus,
.cep-form-input:focus {
  border-color: #0D9488;
}

/* content-editor */
.cep-content-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cep-regen-row {
  display: flex;
}

.cep-regen-input {
  width: 100%;
  box-sizing: border-box;
}

.cep-lang-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cep-lang-name {
  font-size: 12px;
  font-weight: 600;
  color: #333;
}

.cep-lang-textarea {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  color: #1a1a2e;
  resize: vertical;
  outline: none;
  line-height: 1.5;
}

.cep-lang-textarea:focus {
  border-color: #0D9488;
}

.cep-lang-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.cep-char-count {
  font-size: 11px;
  color: #888;
  font-family: monospace;
}

.cep-flag-ok {
  background: #e6f9ec;
  color: #1a7a3c;
  border: 1px solid #b2dfca;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
}

/* upload */
.cep-media {
  max-width: 280px;
}

.cep-upload {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cep-upload-zone {
  border: 1.5px dashed #ccc;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  text-align: center;
  transition: border-color 0.15s;
}

.cep-upload-zone:hover {
  border-color: #0D9488;
}

.cep-upload-icon {
  font-size: 24px;
  color: #aaa;
}

.cep-upload-link {
  color: #0D9488;
}

.cep-upload-input {
  display: none;
}

.cep-upload-skip {
  align-self: flex-start;
}

.cep-link {
  display: inline-block;
  margin-top: 4px;
  color: #0D9488;
  font-weight: 600;
  text-decoration: none;
}

.cep-link:hover {
  text-decoration: underline;
}

/* typing indicator */
.cep-typing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;
}

.cep-typing-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #0D9488;
  animation: cep-typing-bounce 1.2s infinite ease-in-out;
}

.cep-typing-dot:nth-child(2) { animation-delay: 0.2s; }
.cep-typing-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes cep-typing-bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}
</style>



// WEBPACK FOOTER //
// SystemMessage.vue?6a72080b